import { useState, useEffect, useContext } from 'react'
import { useApolloClient } from '@apollo/client'

import { logger } from '@cred/fe-logger'

import InitialValuesContext from 'components/initial-values-context'

import {
  getVariables,
  isObjectWithKeys,
  getPurchaseUuidFromLocalStorage,
  getRefiUuidFromLocalStorage,
  storeSubmissionInLocalStorage,
  checkForAffectKeys,
  getPurchaseLenderCentricUuidFromLocalStorage,
  extractLoanTermsValues,
} from '../../common/utils'
import {
  getPurchasePrefillData,
  getRefinancePrefillData,
  getNewSubmissionProducts,
} from '../../api/apollo-helpers'
import { PURCHASE_TYPE, REFINANCE_TYPE, DEFAULT_TERMS_ARR } from '../../common/constants'

const loanTypeConfig = {
  Conventional: 'conforming',
  FHA: 'fha',
}

const getLoanTypes = (formTypes) => {
  if (!formTypes) {
    return ['conforming']
  }
  const loanTypesFromForm = Object.keys(formTypes || {})

  const res = loanTypesFromForm.reduce((acc, loanType) => {
    if (formTypes[loanType]) {
      acc.push(loanTypeConfig[loanType])
    }
    return acc
  }, [])
  return res && res?.length ? res : ['conforming']
}

/*
  This is an extracted module that will handle all api-related state management. Below are some details about
  how the hook works and its lifecycle.

  1. Initial component mount will cause us to go fetch the necessary data to render the component. This includes
  querying for form data that is stored by the mortgages-api service as well as the returned products for that
  form data. Data and products for each type -- purchase and refinance -- are then stored in state.

  Note, there are two different types of queries that can run on initialize. We check to see if there is a UUID stored
  for each vertical in localStorage. If so, we use that uuid value to get the most recent submission for form data
  and products. If not, we run the default query to get the default form data and products for each vertical. A user
  can have a UUID stored for one vertical and not the other.

  1.1. On first load when UUID stored fails to get products with `Couldn't find Widget::Submission` getPurchasePrefillData/getRefinancePrefillData runs with uuid=null

  2. Once the initialization queries complete, the form is updated using the returned `formValues` state. This value is
  used to initialize the redux form 'FilterForm' component.

  3. When a user submits an update, the following occurs. An initial mutation is sent to update the stored form data for that
  user. That mutation will return a state of the submission. In most cases it will be processing via the 'eligiblityInProgress'
  status. If that's the case then we run another query that takes the uuid returned and queries for products of that UUID. All
  this is abstracted via the `getNewSubmissionProducts` function which returns a promise when the entire process is complete. Once
  the promise is resolved, the state of products for that vertical will be updates as well as the initial form values which need
  to be updated for the filter text to render correctly.
*/
const useProductsAndFormHandler = ({
  initialValues,
  partnerFormData,
  setSubmission = () => {},
  withLenderCentricProducts = false,
  withBestRefinanceProducts = false,
  type = null,
  isMultiVertical = false,
}) => {
  const loanTypes = isMultiVertical ? undefined : getLoanTypes(type)

  const [formValues, setFormValues] = useState(initialValues)
  const [currentProducts, setCurrentProducts] = useState({
    [PURCHASE_TYPE]: null,
    [REFINANCE_TYPE]: null,
    purchaseLenderCentricProducts: null,
    refinanceBestProducts: null,
  })
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [loadingPurchProductsAfterError, setLoadingPurchProductsAfterError] = useState(false)
  const [
    loadingPurchLenderCentricProductsAfterError,
    setLoadingPurchLeenderCentricProductsAfterError,
  ] = useState(false)
  const [loadingRefiProductsAfterError, setLoadingRefiProductsAfterError] = useState(false)
  const [error, setError] = useState(null)

  const { loanType, term } = initialValues
  const client = useApolloClient()

  // Initial values can change due to the async nature of the options provider.
  // If a partner passes this data it will take a few ticks to get to the component,
  // therefore we need to watch for any changes and updated the form accordingly.
  useEffect(() => {
    setFormValues((initialFormValues) => ({
      ...initialFormValues,
      ...initialValues,
    }))
  }, [loanType, JSON.stringify(term)])

  const submitFormValues = (formData, lenderCentricProducts) => {
    const multiVerticalTermsValues = initialValues?.terms || DEFAULT_TERMS_ARR
    const lendersBestTermsValues =
      extractLoanTermsValues(formData?.term) || multiVerticalTermsValues
    const terms = withLenderCentricProducts ? lendersBestTermsValues : multiVerticalTermsValues

    setError(null)
    setLoadingProducts(true)

    if (formData?.loanType === PURCHASE_TYPE) {
      const allRatesNewSubmissionPromise = getNewSubmissionProducts(
        client,
        formData,
        terms,
        undefined,
        undefined,
        loanTypes,
      )
        .then((res) => {
          const { uuid, products, loanType: submissionLoanType } = res
          setSubmission({ submission: res, vertical: submissionLoanType })
          storeSubmissionInLocalStorage(uuid, submissionLoanType)
          setCurrentProducts((originalCurrentProducts) => ({
            ...originalCurrentProducts,
            [submissionLoanType]: products,
          }))
        })
        .catch((err) => {
          setError(err)
        })

      let lenderBestNewSubmissionPromise
      if (withLenderCentricProducts) {
        lenderBestNewSubmissionPromise = getNewSubmissionProducts(
          client,
          formData,
          terms,
          true,
          undefined,
          loanTypes,
          true,
        )
          .then((res) => {
            const { uuid, products, loanType: submissionLoanType } = res
            setSubmission({ submission: res, vertical: submissionLoanType })
            storeSubmissionInLocalStorage(uuid, submissionLoanType, true)
            setCurrentProducts((originalCurrentProducts) => ({
              ...originalCurrentProducts,
              purchaseLenderCentricProducts: products,
            }))
          })
          .catch((err) => {
            setError(err)
          })
      }
      Promise.allSettled(
        [allRatesNewSubmissionPromise, lenderBestNewSubmissionPromise].filter(
          (promise) => promise !== undefined,
        ),
      )
        .then(() => {
          setFormValues({ ...formData })
        })
        .catch((e) => {
          /* eslint-disable-next-line */
          console.log(e)
          setError(e)
        })
        .finally(() => {
          setLoadingProducts(false)
        })
    } else if (formData?.loanType === REFINANCE_TYPE && withBestRefinanceProducts) {
      getNewSubmissionProducts(client, formData, terms, undefined, true, loanTypes)
        .then((res) => {
          const { uuid, products, loanType: submissionLoanType } = res
          setSubmission({ submission: res, vertical: submissionLoanType })
          storeSubmissionInLocalStorage(uuid, submissionLoanType)
          setCurrentProducts((originalCurrentProducts) => ({
            ...originalCurrentProducts,
            refinanceBestProducts: products,
          }))
          setFormValues({ ...formData })
        })
        .catch((e) => {
          /* eslint-disable-next-line */
          console.log(e)
          setError(e)
        })
        .finally(() => {
          setLoadingProducts(false)
        })
      getNewSubmissionProducts(client, formData, terms, undefined, undefined, loanTypes)
        .then((res) => {
          const { uuid, products, loanType: submissionLoanType } = res
          setSubmission({ submission: res, vertical: submissionLoanType })
          storeSubmissionInLocalStorage(uuid, submissionLoanType)
          setCurrentProducts((originalCurrentProducts) => ({
            ...originalCurrentProducts,
            [REFINANCE_TYPE]: products,
          }))
          setFormValues({ ...formData })
        })
        .catch((e) => {
          /* eslint-disable-next-line */
          console.log(e)
          setError(e)
        })
        .finally(() => {
          setLoadingProducts(false)
        })
    } else {
      getNewSubmissionProducts(client, formData, terms, lenderCentricProducts, undefined)
        .then((res) => {
          const { uuid, products, loanType: submissionLoanType } = res
          setSubmission({ submission: res, vertical: submissionLoanType })
          storeSubmissionInLocalStorage(uuid, submissionLoanType)
          setCurrentProducts((originalCurrentProducts) => ({
            ...originalCurrentProducts,
            [submissionLoanType]: products,
          }))
          setFormValues({ ...formData })
        })
        .catch((e) => {
          /* eslint-disable-next-line */
          console.log(e)
          setError(e)
        })
        .finally(() => {
          setLoadingProducts(false)
        })
    }
  }

  const initializePurchaseForm = (
    { ficoType, zipCode, estimatedValue, downPayment, occupancyType, propertyType, products = [] },
    { lenderCentricProducts } = {},
  ) => {
    setFormValues((initialFormValues) => ({
      ...initialFormValues,
      ficoType,
      zipCode,
      occupancyType,
      propertyType,
      estimatedValue,
      downPayment,
      purchaseUuid: getPurchaseUuidFromLocalStorage(),
    }))

    setCurrentProducts((originalCurrentProducts) => {
      return {
        ...originalCurrentProducts,
        [lenderCentricProducts ? 'purchaseLenderCentricProducts' : PURCHASE_TYPE]: products,
      }
    })
  }

  const initializeRefiForm = (
    { ficoType, zipCode, homeValue, loanAmount, occupancyType, propertyType, products, cashOut },
    { bestProducts } = {},
  ) => {
    setFormValues((initialFormValues) => ({
      ...initialFormValues,
      ficoTypeRefi: ficoType,
      zipCodeRefi: zipCode,
      occupancyTypeRefi: occupancyType,
      propertyTypeRefi: propertyType,
      homeValue,
      loanAmount,
      refiUuid: getRefiUuidFromLocalStorage(),
      cashOut: cashOut || 0,
    }))

    setCurrentProducts((originalCurrentProducts) => ({
      ...originalCurrentProducts,
      [bestProducts ? 'refinanceBestProducts' : REFINANCE_TYPE]: products,
    }))
  }
  const { isJumbo } = useContext(InitialValuesContext) || {}

  useEffect(() => {
    const isPartnerDataExist = isObjectWithKeys(partnerFormData)
    const purchaseUuid = getPurchaseUuidFromLocalStorage()
    const refiUuid = getRefiUuidFromLocalStorage()
    const purchaseLenderCentricUuid = getPurchaseLenderCentricUuidFromLocalStorage()
    const zipCode = isPartnerDataExist ? partnerFormData.zipCode : null

    const mortgageCombinedProductTypes = extractLoanTermsValues(initialValues?.term)
    const multiVerticalProductTypes =
      isPartnerDataExist && partnerFormData.terms
        ? partnerFormData.terms.split(',')
        : DEFAULT_TERMS_ARR

    const productTypes = withLenderCentricProducts
      ? mortgageCombinedProductTypes
      : multiVerticalProductTypes

    const isObjectWithAffectKeys = isPartnerDataExist ? checkForAffectKeys(partnerFormData) : false
    const conformingType = isJumbo ? 'nonConforming' : undefined

    const purchasePrefillPromise =
      !purchaseUuid && isPartnerDataExist && isObjectWithAffectKeys
        ? getNewSubmissionProducts(
            client,
            getVariables(partnerFormData, PURCHASE_TYPE),
            productTypes,
            false,
            undefined,
            loanTypes,
          )
            .then((data) => {
              const { uuid, loanType: submissionLoanType } = data
              storeSubmissionInLocalStorage(uuid, submissionLoanType)
              return data
            })
            .then(initializePurchaseForm)
        : getPurchasePrefillData({
            client,
            uuid: purchaseUuid,
            zipCode,
            productTypes,
            loanType: conformingType,
            loanTypes,
          })
            .then((data) => {
              setSubmission({ submission: data, vertical: PURCHASE_TYPE })
              initializePurchaseForm(data)
            })
            .catch((getPurchasePrefillDataError) => {
              const errorMessage = getPurchasePrefillDataError?.message
              if (
                errorMessage === `Couldn't find Widget::Submission` ||
                errorMessage === 'No products found'
              ) {
                setLoadingPurchProductsAfterError(true)
                getPurchasePrefillData({
                  client,
                  uuid: null,
                  zipCode,
                  productTypes,
                  loanType: conformingType,
                  loanTypes,
                })
                  .then((data) => {
                    setLoadingPurchProductsAfterError(false)
                    storeSubmissionInLocalStorage(data?.uuid, data?.vertical)
                    setSubmission({ submission: data, vertical: PURCHASE_TYPE })
                    initializePurchaseForm(data)
                  })
                  .finally(() => {
                    setLoadingPurchProductsAfterError(false)
                  })
              } else {
                logger.error(
                  'purchasePrefillPromise > getPurchasePrefillData',
                  { purchaseUuid, zipCode, productTypes, conformingType },
                  'Mortgage rates table widget. Error while getPurchasePrefillData',
                  getPurchasePrefillDataError,
                )
              }
            })

    let purchaseLenderCentricProductsPromise
    if (withLenderCentricProducts) {
      purchaseLenderCentricProductsPromise =
        !purchaseLenderCentricUuid && isPartnerDataExist && isObjectWithAffectKeys
          ? getNewSubmissionProducts(
              client,
              getVariables(partnerFormData, PURCHASE_TYPE),
              productTypes,
              true,
              undefined,
              loanTypes,
            )
              .then((data) => {
                const { uuid, loanType: submissionLoanType } = data
                storeSubmissionInLocalStorage(uuid, submissionLoanType, true)
                return data
              })
              .then((data) => {
                initializePurchaseForm(data, {
                  lenderCentricProducts: true,
                })
              })
          : getPurchasePrefillData({
              client,
              uuid: purchaseLenderCentricUuid,
              zipCode,
              productTypes,
              loanType: conformingType,
              lenderCentricProducts: true,
              loanTypes,
            })
              .then((data) => {
                setSubmission({ submission: data, vertical: PURCHASE_TYPE })
                initializePurchaseForm(data, {
                  lenderCentricProducts: true,
                })
              })
              .catch((getPurchasePrefillDataError) => {
                const errorMessage = getPurchasePrefillDataError?.message
                if (
                  errorMessage === `Couldn't find Widget::Submission` ||
                  errorMessage === 'No products found'
                ) {
                  setLoadingPurchLeenderCentricProductsAfterError(true)
                  getPurchasePrefillData({
                    client,
                    uuid: null,
                    zipCode,
                    productTypes,
                    loanType: conformingType,
                    lenderCentricProducts: true,
                    loanTypes,
                  })
                    .then((data) => {
                      setLoadingPurchLeenderCentricProductsAfterError(false)
                      storeSubmissionInLocalStorage(data?.uuid, data?.vertical, true)
                      setSubmission({ submission: data, vertical: PURCHASE_TYPE })
                      initializePurchaseForm(data, { lenderCentricProducts: true })
                    })
                    .finally(() => {
                      setLoadingPurchLeenderCentricProductsAfterError(false)
                    })
                } else {
                  logger.error(
                    'purchaseLenderCentricProductsPromise > getPurchasePrefillData',
                    { purchaseUuid, zipCode, productTypes, conformingType },
                    'Mortgage rates table widget. Error while getPurchasePrefillData lender centric products',
                    getPurchasePrefillDataError,
                  )
                }
              })
    }

    const refiPrefillPromise =
      !refiUuid && isPartnerDataExist && isObjectWithAffectKeys
        ? getNewSubmissionProducts(
            client,
            getVariables(partnerFormData, REFINANCE_TYPE),
            productTypes,
          )
            .then((data) => {
              const { uuid, loanType: submissionLoanType } = data
              storeSubmissionInLocalStorage(uuid, submissionLoanType)
              return data
            })
            .then(initializeRefiForm)
        : getRefinancePrefillData(client, refiUuid, zipCode, productTypes, conformingType)
            .then((data) => {
              setSubmission({ submission: data, vertical: REFINANCE_TYPE })
              initializeRefiForm(data)
            })
            .catch((getRefinancePrefillDataError) => {
              const errorMessage = getRefinancePrefillDataError?.message
              if (
                errorMessage === `Couldn't find Widget::Submission` ||
                errorMessage === 'No products found'
              ) {
                setLoadingRefiProductsAfterError(true)
                getRefinancePrefillData(client, null, zipCode, productTypes, conformingType)
                  .then((data) => {
                    setLoadingRefiProductsAfterError(false)
                    storeSubmissionInLocalStorage(data?.uuid, data?.vertical)
                    setSubmission({ submission: data, vertical: REFINANCE_TYPE })
                    initializeRefiForm(data)
                  })
                  .finally(() => {
                    setLoadingRefiProductsAfterError(false)
                  })
              } else {
                logger.error(
                  'refiPrefillPromise > getRefinancePrefillData',
                  { purchaseUuid, zipCode, productTypes, conformingType },
                  'Mortgage rates table widget. Error while getRefinancePrefillData',
                  getRefinancePrefillDataError,
                )
              }
            })

    let bestRefinanceProductsPromise

    if (withBestRefinanceProducts) {
      bestRefinanceProductsPromise =
        !refiUuid && isPartnerDataExist && isObjectWithAffectKeys
          ? getNewSubmissionProducts(
              client,
              getVariables(partnerFormData, REFINANCE_TYPE),
              productTypes,
              undefined,
              true,
              loanTypes,
            )
              .then((data) => {
                const { uuid, loanType: submissionLoanType } = data
                storeSubmissionInLocalStorage(uuid, submissionLoanType)
                return data
              })
              .then((data) => {
                initializeRefiForm(data, { bestProducts: true })
              })
          : getRefinancePrefillData(client, refiUuid, zipCode, productTypes, conformingType, true)
              .then((data) => {
                setSubmission({ submission: data, vertical: REFINANCE_TYPE })
                initializeRefiForm(data, { bestProducts: true })
              })
              .catch((getRefinancePrefillDataError) => {
                const errorMessage = getRefinancePrefillDataError?.message
                if (
                  errorMessage === `Couldn't find Widget::Submission` ||
                  errorMessage === 'No products found'
                ) {
                  setLoadingRefiProductsAfterError(true)
                  getRefinancePrefillData(client, null, zipCode, productTypes, conformingType, true)
                    .then((data) => {
                      setLoadingRefiProductsAfterError(false)
                      storeSubmissionInLocalStorage(data?.uuid, data?.vertical)
                      setSubmission({ submission: data, vertical: REFINANCE_TYPE })
                      initializeRefiForm(data, { bestProducts: true })
                    })
                    .finally(() => {
                      setLoadingRefiProductsAfterError(false)
                    })
                } else {
                  logger.error(
                    'bestRefinanceProductsPromise > bestRefinanceProductsPromise',
                    { purchaseUuid, zipCode, productTypes, conformingType },
                    'Mortgage rates table widget. Error while getRefinancePrefillData',
                    getRefinancePrefillDataError,
                  )
                }
              })
    }

    Promise.allSettled(
      [
        refiPrefillPromise,
        purchasePrefillPromise,
        purchaseLenderCentricProductsPromise,
        bestRefinanceProductsPromise,
      ].filter((promise) => promise !== undefined),
    )
      .then((res) => {
        /* eslint-disable-next-line */
        console.log('Promise.allSettled: ', { res })
      })
      .catch((e) => {
        /* eslint-disable-next-line */
        console.log('Promise.allSettled: error ', e)
        setError(e)
      })
      .finally(() => {
        setLoadingProducts(false)
      })
  }, [])

  const overallLoading =
    loadingProducts ||
    loadingPurchProductsAfterError ||
    loadingRefiProductsAfterError ||
    loadingPurchLenderCentricProductsAfterError

  return [submitFormValues, formValues, currentProducts, overallLoading, error]
}

export default useProductsAndFormHandler
